/**
 * Seleccion de la reserva y su reflejo en la URL.
 *
 * La URL guarda **solo lo que no es personal**: dia, cancha, hora y duracion. Con eso
 * una persona puede compartir un enlace a "la techada el sabado a las 20:00" o recargar
 * sin perder lo que eligio.
 *
 * **Nunca** viaja un dato personal en la URL (docs/SEGURIDAD.md §6). Una direccion queda
 * en el historial del navegador, en el `Referer` de cualquier recurso externo y en los
 * registros de cualquier proxy intermedio. `CLAVES_URL` es la lista cerrada de lo que se
 * puede escribir, y hay una prueba que la verifica.
 */

import { CANCHAS, DURACIONES_MINUTOS, type CanchaId, type DuracionMinutos } from '@padel/domain';

/** Lo unico que la URL puede llevar. Lista cerrada a proposito. */
export const CLAVES_URL = ['fecha', 'cancha', 'hora', 'duracion'] as const;

/** Estado de la eleccion de turno. `null` significa "todavia no elegido". */
export interface Seleccion {
  readonly fecha: string;
  readonly canchaId: CanchaId | null;
  readonly localHora: string | null;
  readonly duracionMinutos: DuracionMinutos | null;
}

const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;
const FORMATO_HORA = /^([01]\d|2[0-3]):(00|30)$/;

function comoCancha(valor: string | null): CanchaId | null {
  return CANCHAS.find((cancha) => cancha === valor) ?? null;
}

function comoDuracion(valor: string | null): DuracionMinutos | null {
  const numero = Number(valor);
  return DURACIONES_MINUTOS.find((duracion) => duracion === numero) ?? null;
}

/**
 * Lee la seleccion de la URL, descartando en silencio cualquier valor que no sea de la
 * forma esperada. Un parametro manipulado no puede hacer que la pantalla pida un horario
 * imposible: como mucho, la seleccion queda vacia.
 */
export function leerSeleccion(parametros: URLSearchParams, fechaPorDefecto: string): Seleccion {
  const fecha = parametros.get('fecha');

  return {
    fecha: fecha !== null && FORMATO_FECHA.test(fecha) ? fecha : fechaPorDefecto,
    canchaId: comoCancha(parametros.get('cancha')),
    localHora: FORMATO_HORA.test(parametros.get('hora') ?? '') ? parametros.get('hora') : null,
    duracionMinutos: comoDuracion(parametros.get('duracion')),
  };
}

/** Serializa la seleccion. Solo emite las claves de `CLAVES_URL`. */
export function escribirSeleccion(seleccion: Seleccion): URLSearchParams {
  const parametros = new URLSearchParams();
  parametros.set('fecha', seleccion.fecha);

  if (seleccion.canchaId !== null) {
    parametros.set('cancha', seleccion.canchaId);
  }
  if (seleccion.localHora !== null) {
    parametros.set('hora', seleccion.localHora);
  }
  if (seleccion.duracionMinutos !== null) {
    parametros.set('duracion', String(seleccion.duracionMinutos));
  }

  return parametros;
}

/** La seleccion identifica un turno completo y se puede reservar. */
export interface TurnoElegido {
  readonly fecha: string;
  readonly canchaId: CanchaId;
  readonly localHora: string;
  readonly duracionMinutos: DuracionMinutos;
}

/** Estrecha una seleccion parcial a un turno completo, o devuelve `null`. */
export function turnoDe(seleccion: Seleccion): TurnoElegido | null {
  if (
    seleccion.canchaId === null ||
    seleccion.localHora === null ||
    seleccion.duracionMinutos === null
  ) {
    return null;
  }
  return {
    fecha: seleccion.fecha,
    canchaId: seleccion.canchaId,
    localHora: seleccion.localHora,
    duracionMinutos: seleccion.duracionMinutos,
  };
}
